/* eslint-disable quote-props */
import { Measure, Unit } from '../types'
import { ROUTES } from '../routes/routes'
import { SEOHelmetProps } from '../components/SEOHelmet'
import { fetchEnvironmentVar } from './miscUtils'

// Default SEO values for static routes
export const DEFAULT_SEO: Record<string, SEOHelmetProps> = {
  HOME: {
    title: 'Top Converters – Convert Any Measurement',
    description: 'Free online unit converter tool. Convert between different units of measurement for length, weight, volume, temperature, and more.',
    keywords: ['unit converter', 'measurement conversion', 'convert units', 'top converters'],
    robots: 'index, follow'
  },
  CONTACT_US: {
    title: 'Contact Us – Top Converters',
    description: 'Get in touch with our team for any questions or feedback about our unit conversion tools.',
    keywords: ['contact', 'support', 'feedback', 'unit converter', 'top converters']
  },
  ABOUT_US: {
    title: 'About Us – Top Converters',
    description: 'Learn about our mission to provide accurate and easy-to-use unit conversion tools for everyone.',
    keywords: ['about', 'unit converter', 'company information', 'top converters']
  },
  NOT_FOUND: {
    title: '404 Page Not Found – Top Converters',
    description: 'The page you are looking for could not be found.',
    robots: 'noindex, nofollow'
  }
}

// Generator for breadcrumb structured data
export const generateBreadcrumbStructuredData = (
  breadcrumbs: Array<{ name: string, url: string }>
): object => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbs.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': breadcrumb.name,
      'item': breadcrumb.url
    }))
  }
}


export const generateMeasurePageSEOProps = (measure: Measure, defaultFromUnit: Unit, defaultToUnit: Unit): SEOHelmetProps => {
  const baseUrl = fetchEnvironmentVar('REACT_APP_SITE_URL')
  const title = `${measure.displayName} Converter – Top Converters`

  const description = `Convert between various ${measure.displayName.toLowerCase()} units like ${Object.values(measure.units).slice(0, 3).map((unit: Unit) => unit.displayName).join(', ')} and more.`

  const keywords = [
    `${measure.displayName.toLowerCase()} converter`,
    `convert ${measure.displayName.toLowerCase()} units`,
    `${measure.displayName.toLowerCase()} calculator`
  ]

  const canonicalUrl = generateCanonicalUrl(ROUTES.CONVERTER.create(measure, defaultFromUnit, defaultToUnit))

  // Create breadcrumb data that gets displayed in the search engine results
  const breadcrumbsData = generateBreadcrumbStructuredData([
    { name: 'Home', url: `${baseUrl}${ROUTES.HOME}` },
    { name: `${measure.displayName} Converter`, url: canonicalUrl }
  ])

  const robots = 'index, follow'

  return {
    title,
    description,
    keywords,
    canonicalUrl,
    ogTitle: title,
    ogDescription: description,
    structuredData: breadcrumbsData,
    robots
  }
}


export const generateConverterPageSEOProps = (measure: Measure, fromUnit: Unit, toUnit: Unit): SEOHelmetProps => {
  const baseUrl = fetchEnvironmentVar('REACT_APP_SITE_URL')
  const title = `${fromUnit.displayName} to ${toUnit.displayName} Converter – Top Converters`

  const description = `Free online ${measure.displayName.toLowerCase()} converter from ${fromUnit.displayName} to ${toUnit.displayName}. Quick and easy ${measure.displayName.toLowerCase()} conversion with precise calculations and formulas.`

  const keywords = [
    `${fromUnit.displayName.toLowerCase()} to ${toUnit.displayName.toLowerCase()} converter`,
    `convert ${fromUnit.displayName.toLowerCase()} to ${toUnit.displayName.toLowerCase()}`,
    `${measure.displayName.toLowerCase()} conversion`,
    `${fromUnit.symbol} to ${toUnit.symbol} converter`,
    `convert ${fromUnit.symbol} to ${toUnit.symbol}`
  ]

  const canonicalUrl = generateCanonicalUrl(ROUTES.CONVERTER.create(measure, fromUnit, toUnit))

  // Create breadcrumb data that gets displayed in the search engine results
  const breadcrumbsData = generateBreadcrumbStructuredData([
    { name: 'Home', url: `${baseUrl}${ROUTES.HOME}` },
    { name: `${measure.displayName} Converter`, url: `${baseUrl}${ROUTES.MEASURE.create(measure)}` },
    { name: `${fromUnit.displayName} to ${toUnit.displayName}`, url: canonicalUrl }
  ])

  const robots = 'index, follow'

  return {
    title,
    description,
    keywords,
    canonicalUrl,
    ogTitle: title,
    ogDescription: description,
    structuredData: breadcrumbsData,
    robots
  }
}

export const generateCanonicalUrl = (route: string): string => {
  const baseUrl = fetchEnvironmentVar('REACT_APP_SITE_URL')
  return `${baseUrl}${route}`
}
